import { useEffect, useState } from 'react';
import { AlertTriangle, HelpCircle } from 'lucide-react';

/**
 * In-page confirmation dialog, replacing the browser's window.confirm().
 *
 * Usage anywhere in the app:
 *
 *     if (!(await confirmDialog({ message: 'Render 6 images?' }))) return;
 *
 * `ConfirmHost` is mounted once in App.tsx; `confirmDialog()` talks to it
 * through a module-level channel, so call sites need no context or props.
 * If the host is somehow not mounted, it degrades to window.confirm rather
 * than silently approving anything.
 */

export interface ConfirmOptions {
  /** Short heading; defaults to "Are you sure?" */
  title?: string;
  /** Body text. Newlines are preserved. */
  message: string;
  /** Label on the confirming button; defaults to "Confirm". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button red for destructive actions. */
  danger?: boolean;
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let deliver: ((p: Pending) => void) | null = null;

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    if (!deliver) {
      resolve(window.confirm(opts.message));
      return;
    }
    deliver({ ...opts, resolve });
  });
}

export function ConfirmHost() {
  const [pending, setPending] = useState<Pending | null>(null);

  useEffect(() => {
    deliver = (p) => {
      // A dialog already open loses to the new one; deny the old request so
      // its caller doesn't hang forever.
      setPending((current) => {
        current?.resolve(false);
        return p;
      });
    };
    return () => {
      deliver = null;
    };
  }, []);

  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  const close = (ok: boolean) => {
    pending?.resolve(ok);
    setPending(null);
  };

  if (!pending) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={() => close(false)}
    >
      <div
        className="bg-white rounded-3xl border border-natural-border shadow-2xl w-full max-w-sm p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <div
            className={`p-2 rounded-xl shrink-0 ${
              pending.danger ? 'bg-red-50 text-red-500' : 'bg-natural-sage/10 text-natural-sage'
            }`}
          >
            {pending.danger ? <AlertTriangle size={18} /> : <HelpCircle size={18} />}
          </div>
          <div className="min-w-0 space-y-1.5">
            <h3 className="font-serif text-base font-medium leading-snug">
              {pending.title ?? 'Are you sure?'}
            </h3>
            <p className="text-xs text-natural-text/65 leading-relaxed whitespace-pre-line">
              {pending.message}
            </p>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-1">
          <button onClick={() => close(false)} className="zen-btn-ghost py-2 px-4 text-xs" autoFocus>
            {pending.cancelLabel ?? 'Cancel'}
          </button>
          <button
            onClick={() => close(true)}
            className={
              pending.danger
                ? 'zen-btn-danger py-2 px-4 text-xs'
                : 'zen-btn-primary py-2 px-4 text-xs'
            }
          >
            {pending.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
